import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import Lightbox from '../components/Lightbox';
import OrderForm from '../components/OrderForm';
import { imageUrl } from '../lib/images';
import { fetchCollection, FALLBACK_REFERENCER, getSlug } from '../lib/cms';

export default function ReferenceDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    async function loadProject() {
      setLoading(true);
      const data = await fetchCollection('referencer', FALLBACK_REFERENCER);
      const found = (data || FALLBACK_REFERENCER).find(
        (p) => getSlug(p) === slug || p.slug === slug || (typeof p.slug === 'object' && p.slug.da === slug)
      );
      setProject(found || null);
      setLoading(false);
    }
    loadProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="flex items-center gap-3">
          <i className="fa-solid fa-circle-notch animate-spin text-sky-400 text-2xl" aria-hidden="true" />
          <span className="text-slate-300">Indlæser projekt...</span>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-50 py-24 px-4 text-center">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Projektet blev ikke fundet</h1>
        <p className="text-slate-600 mb-8">Vi kunne desværre ikke finde det ønskede referenceprojekt.</p>
        <Link
          to="/referencer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-900 text-white font-semibold text-sm"
        >
          <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
          <span>Tilbage til referencer</span>
        </Link>
      </div>
    );
  }

  const images = project.billede || [];

  return (
    <div className="bg-slate-50 min-h-screen">
      <PageMeta
        title={`${project.titel} - Referencer - Duus Rustfri`}
        description={`Se referencen ${project.titel} udført af Duus Rustfri. Opgavetype: ${project.opgavetype}.`}
      />

      {/* Header Banner */}
      <section className="bg-slate-950 text-white py-16 sm:py-20 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link
              to="/referencer"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-400 hover:text-sky-300 transition"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>Tilbage til alle referencer</span>
            </Link>
          </div>

          <div className="max-w-3xl">
            <div className="inline-block px-3 py-1 rounded-full bg-slate-900 text-sky-400 border border-slate-800 text-xs font-mono font-semibold uppercase mb-3">
              {project.opgavetype}
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-4">
              {project.titel}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
              {project.lokation && (
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-location-dot text-sky-400" aria-hidden="true" />
                  <span>{project.lokation}</span>
                </span>
              )}
              {project.materiale && (
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-layer-group text-sky-400" aria-hidden="true" />
                  <span>{project.materiale}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left: Gallery of project photos */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200 shadow-md cursor-pointer group"
                    onClick={() => {
                      setCurrentImgIndex(idx);
                      setLightboxOpen(true);
                    }}
                  >
                    <img
                      src={imageUrl(img, 'width=1000,quality=85,format=auto')}
                      alt={`${project.titel} foto ${idx + 1}`}
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                      <span className="w-12 h-12 rounded-full bg-slate-900/80 border border-slate-700 flex items-center justify-center text-base">
                        <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Description & Inquiry */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm space-y-6">
                <h2 className="text-xl font-bold text-slate-950">
                  Beskrivelse af projektet
                </h2>

                <div
                  className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-sm sm:text-base space-y-4"
                  dangerouslySetInnerHTML={{ __html: project.beskrivelse }}
                />

                <div className="pt-4 border-t border-slate-100 space-y-2 text-xs">
                  {project.materiale && (
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">Materiale:</span>
                      <span className="font-semibold text-slate-900">{project.materiale}</span>
                    </div>
                  )}
                  {project.opgavetype && (
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">Kategori:</span>
                      <span className="font-semibold text-slate-900">{project.opgavetype}</span>
                    </div>
                  )}
                  {project.lokation && (
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">Lokation:</span>
                      <span className="font-semibold text-slate-900">{project.lokation}</span>
                    </div>
                  )}
                </div>

                <div className="pt-4">
                  <a
                    href="#bestil"
                    className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition shadow-sm"
                  >
                    <span>Forespørg på lignende opgave</span>
                    <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="bestil" className="py-16 sm:py-24 bg-slate-950 text-white scroll-mt-20 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <OrderForm
            defaultOpgavetype={project.titel}
            title={`Forespørgsel med reference: ${project.titel}`}
            subtitle="Ønsker du en lignende løsning? Send dine mål og ønsker herunder."
          />
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        images={images}
        captions={[project.titel]}
        currentIndex={currentImgIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setCurrentImgIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))}
        onNext={() => setCurrentImgIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))}
        onSelectIndex={(idx) => setCurrentImgIndex(idx)}
      />
    </div>
  );
}
